import { type Components, type Theme } from '@mui/material';
import { isColorWhiteListed } from 'common/components/Buttons/Button/utils/isColorWhiteListed';

import { common, grey, red } from '../palette';

export const MuiButton: Components<Theme>['MuiButton'] = {
  defaultProps: {
    disableElevation: true,
    variant: 'contained',
  },
  styleOverrides: {
    root: ({ theme: { palette, typography }, ownerState: { color } }) => ({
      textTransform: 'none',
      ...typography['natter-text-sm'],
      fontWeight: 500,
      flexShrink: 0,
      whiteSpace: 'nowrap',

      ...(color &&
        isColorWhiteListed(color) && {
          '&:disabled.MuiLoadingButton-loading': {
            backgroundColor: palette[color].main,

            '.MuiLoadingButton-loadingIndicator': {
              color: palette[color].contrastText,
            },
          },
        }),
    }),
    containedPrimary: ({ theme: { palette } }) => ({
      '&.Mui-focusVisible': {
        backgroundColor: palette.primary.main,
      },

      '&:active': {
        backgroundColor: palette.primary.main,
      },
    }),
    containedSecondary: ({ theme: { palette } }) => ({
      '&.Mui-focusVisible': {
        backgroundColor: grey[300],
      },

      '&:active': {
        backgroundColor: palette.common.white,
        border: `2px solid ${palette.primary.main}`,
      },
    }),
    containedError: {
      '&.Mui-focusVisible': {
        backgroundColor: red[700],
      },

      '&:active': {
        backgroundColor: red[800],
      },
    },
    outlined: ({ theme: { palette }, ownerState: { color } }) => ({
      ...(color && isColorWhiteListed(color) && { borderColor: palette[color].main }),

      '&:hover': {
        backgroundColor: palette.extendedPrimary[25],
      },

      '&.Mui-focusVisible': {
        backgroundColor: palette.extendedPrimary[50],
      },

      '&:active': {
        backgroundColor: palette.extendedPrimary[100],
      },
    }),
    text: ({ theme: { palette }, ownerState: { color } }) => ({
      color: color && isColorWhiteListed(color) ? palette[color].main : palette.grey[700],

      '&:active': {
        backgroundColor: palette.common.white,
      },

      '&:hover': {
        backgroundColor: palette.grey[200],
      },

      '&.Mui-focusVisible': {
        backgroundColor: palette.grey[300],
      },
    }),
    textSecondary: ({ theme: { palette } }) => ({
      '&:active': {
        borderColor: palette.primary.main,
      },
    }),
    textError: ({ theme: { palette } }) => ({
      '&:active': {
        borderColor: palette.error.main,
      },
    }),
    sizeLarge: ({ theme: { spacing, typography } }) => ({
      ...typography['natter-text-md'],

      height: 40,
      minWidth: 54,
      padding: spacing(1, 2),
    }),
    sizeMedium: ({ theme: { spacing, typography } }) => ({
      ...typography['natter-text-sm'],

      height: 32,
      minWidth: 52,
      padding: spacing(0.75, 2),
    }),
    sizeSmall: ({ theme: { spacing, typography } }) => ({
      ...typography['natter-text-xs'],

      height: 24,
      minWidth: 32,
      padding: spacing(0.5, 1),
    }),
    fullWidth: {
      flexShrink: 'initial',
    },
  },
  variants: [
    {
      props: { variant: 'light' },
      style: ({ theme: { palette } }) => ({
        backgroundColor: palette.extendedPrimary[25],
        color: palette.primary.main,

        '&:hover': {
          backgroundColor: palette.extendedPrimary[50],
        },

        '&.Mui-focusVisible': {
          backgroundColor: palette.extendedPrimary[100],
        },

        '&:active; &:focus': {
          backgroundColor: palette.extendedPrimary[200],
        },
      }),
    },
    {
      props: { variant: 'outlined-grey' },
      style: () => ({
        border: '1px solid',
        borderColor: grey[300],
        color: grey[700],
        backgroundColor: common.white,

        '&:hover': {
          backgroundColor: grey[50],
        },

        '&.Mui-focusVisible': {
          backgroundColor: grey[100],
          borderColor: grey[400],
        },

        '&:active; &:focus': {
          backgroundColor: grey[200],
          borderColor: grey[500],
        },
      }),
    },
    {
      props: { variant: 'text-no-padding' },
      style: ({ theme: { palette } }) => ({
        backgroundColor: 'unset',
        color: palette.primary.main,
        height: 'unset',
        padding: 0,
        borderRadius: 0,
        borderColor: 'transparent',
        transition: 'border-color 0ms !important',

        '&:hover': {
          backgroundColor: 'unset',
          borderColor: palette.primary.main,
        },

        '&.Mui-focusVisible': {
          color: palette.extendedPrimary[700],
          borderColor: palette.extendedPrimary[700],
        },

        '&:active; &:focus': {
          color: palette.extendedPrimary[800],
          borderColor: palette.extendedPrimary[800],
        },
      }),
    },
    // `color="inherit"` is the neutral inline action (Skip, No thanks): it takes the colour of the text
    // around it and underlines on hover, where the default is the blue one.
    {
      props: { variant: 'text-no-padding', color: 'inherit' },
      style: {
        color: 'inherit',
        '&:hover': { borderColor: 'currentColor' },
        '&.Mui-focusVisible': { color: 'inherit', borderColor: 'currentColor' },
        '&:active, &:focus': { color: 'inherit', borderColor: 'currentColor' },
      },
    },
  ],
};

interface DeprecatedButtonVariants {
  /** @deprecated */
  grayed?: true;
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides extends DeprecatedButtonVariants {
    light?: true;
    'outlined-grey'?: true;
    'text-no-padding'?: true;
  }

  interface ButtonPropsVariantOverrides extends DeprecatedButtonVariants {
    light?: true;
    'outlined-grey'?: true;
    'text-no-padding'?: true;
  }
}
